<template>
    <div>
        <v-overlay
            :value="isOpenDialog"
            :absolute="true"
            :class="{'blur-overlay': isOpenDialog}"
        />
        <!-- RESOLUTION FORM -->
        <StepHeader
            :subtitle="subtitle"
            :description="description"
        />
        <StepRadioGroup
            class="mt-4"
            :form="qualityForm"
            :selectedValue="selectedValue[qualityForm.id]"
        />
        <!-- /RESOLUTION FORM -->

        <!-- RESOLUTION DISCLAIMER -->
        <label
            v-if="isLowResolution"
            v-html="$t('habiteoStudio.resolutionDisclaimer')"
        />
        <!-- /RESOLUTION DISCLAIMER -->

        <StepHeader
            class="mt-6"
            :subtitle="presetData.title"
            :description="presetData.subtitle"
        />

        <!-- PRESET FORM -->

        <!-- PRESET SLIDER -->
        <v-sheet
            class="mx-auto"
        >
            <v-slide-group
                v-model="selectedPreset"
                class="pt-4 pb-6"
                center-active
                mandatory
                show-arrows
            >
                <v-slide-item
                    v-for="(preset, presetIndex) in presetsfields"
                    :key="preset.id"
                    v-slot="{ active, toggle }"
                >
                    <v-card
                        :class="{
                            'active-card': active,
                            'ml-4': presetIndex > 0
                        }"
                        class="d-flex flex-column pa-2"
                        width="85"
                        :ripple="false"
                        @click="toggle(), preset.id === 'preset_advanced' ? toggleDialog() : null"
                    >
                        <v-img
                            :src="preset.img"
                            width="32"
                            max-height="32"
                            contain
                            aspect-ratio="1"
                        />
                        <span class="legend text-uppercase font-weight-bold disable-select text-center pt-2">
                            {{ preset.label }}
                        </span>
                    </v-card>
                </v-slide-item>
            </v-slide-group>
        </v-sheet>

        <!-- /PRESET SLIDER -->

        <!-- ADVANCED PRESET DIALOG -->
        <v-dialog
            v-model="isOpenDialog"
            width="285"
            class="my-custom-dialog"
            :attach="true"
            hide-overlay
        >
            <v-card class="pa-8">
                <v-card-title class="pa-0 font-weight-bold h2">
                    {{ advancedPreset.title }}
                </v-card-title>

                <v-card-text class="py-6 pa-0">
                    <StepBtnGroup
                        class="my-6 step-group advanced-preset"
                        :fields="panniniFields"
                        :selectedValue="advancedPresetSettings.pannini"
                        :orientation="'row'"
                        @select-value="selectPannini"
                    />

                    <StepSlider
                        :label="fisheye.label"
                        :sublabel="fisheye.sublabel"
                        :min="fisheye.min"
                        :max="fisheye.max"
                        :step="0.05"
                        :defaultValue="advancedPresetSettings.fisheye"
                        @update-slider="selectFisheye"
                    />
                </v-card-text>

                <div class="d-flex justify-end">
                    <v-btn
                        class="green-text darken-2 text-none font-weight-bold text-decoration-underline pa-2"
                        :elevation="0"
                        color="transparent"
                        x-large
                        :ripple="false"
                        @click.prevent="toggleDialog"
                    >
                        <span>
                            {{ $t('utils.validate') }}
                        </span>
                    </v-btn>
                </div>
            </v-card>
        </v-dialog>
        <!-- /ADVANCED PRESET DIALOG -->

        <!-- /PRESET FORM -->
    </div>
</template>

<script>
    import { mapGetters } from "vuex";

    import StepHeader from '@/components/myHabiteoStudio/steps/components/StepHeader';
    import StepRadioGroup from '@/components/myHabiteoStudio/steps/components/StepRadioGroup';
    import StepSlider from "@/components/myHabiteoStudio/steps/components/StepSlider";
    import StepBtn from "@/components/myHabiteoStudio/steps/components/StepBtn";
    import StepBtnGroup from "@/components/myHabiteoStudio/steps/components/StepBtnGroup";

    export default {
        name: 'StepQuality',
        components: {
            StepBtnGroup,
            StepSlider,
            StepHeader,
            StepRadioGroup,
            StepBtn
        },
        props: {
            subtitle: {
                type: String,
                required: true
            },
            description: {
                type: String,
                required: true
            },
            forms: {
                type: Array,
                required: true
            },
            selectedValue: {
                type: Object,
                required: false
            }
        },
        computed: {
            ...mapGetters([
                'isLocalstorageAvailable'
            ]),
            qualityForm() {
                return this.$props.forms.find(el => el.id === 'quality');
            },
            pannini() {
                return this.advancedPreset.fields.find(el => el.id === 'panini');
            },
            fisheye() {
                return this.advancedPreset.fields.find(el => el.id === 'fisheye');
            },
            presetsfields() {
              return this.presetData.inputs.find(el => el.id === 'preset').fields;
            },
            presetData() {
                return this.$props.forms.find(el => el.id === 'preset');
            },
            advancedPreset() {
                return this.presetData.inputs.find(el => el.id === 'advanced_preset');
            },
            advancedPresetKey() {
                return `advanced-preset-${this.$route.query.id}`;
            },
            advancedPresetSettings() {
                return {
                    'pannini': this.selectedPannini,
                    'fisheye': this.selectedFisheye
                };
            },
            isLowResolution() {
                return this.qualityForm.disclaimer === true;
            }
        },
        data() {
            return {
                isOpenDialog: false,
                selectedPreset: null,
                selectedPannini: null,
                selectedFisheye: null,
                panniniFields: []
            }
        },
        mounted() {
            this.selectedPreset = this.selectedValue.preset ? this.selectedValue.preset - 1 : 0;
            if (this.isLocalstorageAvailable) {
                this.selectedPannini = localStorage.getItem(this.advancedPresetKey) ? JSON.parse(localStorage.getItem(this.advancedPresetKey)).pannini : 0;
                this.selectedFisheye = localStorage.getItem(this.advancedPresetKey) ? JSON.parse(localStorage.getItem(this.advancedPresetKey)).fisheye : 0.5;
            }
            this.getPanniniField()
        },
        methods: {
            toggleDialog() {
                this.isOpenDialog = !this.isOpenDialog;
            },
            getPanniniField() {
                let verticalPanniniObject = {
                    'label': this.pannini.label,
                    'value': this.pannini.min
                }
                let horizontalPanniniObject= {
                    'label': this.pannini.sublabel,
                    'value': this.pannini.max
                }
                this.panniniFields.push(verticalPanniniObject);
                this.panniniFields.push(horizontalPanniniObject);
            },
            selectPannini(value) {
                this.selectedPannini = value;
                if (this.isLocalstorageAvailable)
                    this.saveAdvancedPresetSettings();
                this.$emit('set-properties', 'pannini', value);
            },
            setProperties(type, value) {
                this.$emit('set-properties', type, value);
            },
            selectFisheye(value) {
                this.selectedFisheye = value;
                if (this.isLocalstorageAvailable)
                    this.saveAdvancedPresetSettings();
                this.$emit('set-properties', 'fisheye', value);
            },
            saveAdvancedPresetSettings() {
                localStorage.setItem(this.advancedPresetKey, JSON.stringify({
                    'pannini': this.selectedPannini,
                    'fisheye': this.selectedFisheye
                }));
            }
        },
        watch: {
            selectedPreset(val) {
                this.presetData.inputs[0].value = val + 1;
            },
            selectedPannini(val) {
                this.presetData.inputs[1].value = {
                    'pannini': val,
                    'fisheye': this.selectedFisheye
                };
            },
            selectedFisheye(val) {
                this.presetData.inputs[1].value = {
                    'pannini': this.selectedPannini,
                    'fisheye': val
                };
            }
        }
    }
</script>

<style lang="scss">
    @import "@/assets/scss/components/_slider-group.scss";
</style>


// WEBPACK FOOTER //
// src/components/myHabiteoStudio/steps/StepQuality.vue